import React from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Typography,
} from "@mui/material";
import colors from "../../Constants/colors";
import { useComoAgents } from "../../Query/useComoAgent";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";

interface ComoAgentSelectorProps {
  selectedAgentIds: number[];
  onChange: (agentIds: number[]) => void;
  disabled?: boolean;
  // "card": no own title (the surrounding card has one), options in a grid.
  variant?: "default" | "card";
}

const ComoAgentSelector: React.FC<ComoAgentSelectorProps> = ({
  selectedAgentIds,
  onChange,
  disabled = false,
  variant = "default",
}) => {
  const { data: agents = [] } = useComoAgents();
  const { colors: workspace } = useWorkspaceColors();

  const handleToggle = (agentId: number) => {
    if (selectedAgentIds.includes(agentId)) {
      onChange(selectedAgentIds.filter((id) => id !== agentId));
    } else {
      onChange([...selectedAgentIds, agentId]);
    }
  };

  if (agents.length === 0) return null;

  return (
    <Box sx={{ width: "100%", mt: variant === "card" ? 0 : 2 }}>
      {variant === "default" && (
        <Typography
          sx={{
            color: workspace.primary_dark,
            fontWeight: 600,
            fontSize: "0.85rem",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            borderBottom: `1px solid ${colors.gray200}`,
            pb: 0.5,
            mb: 1.5,
          }}
        >
          Virtual Team Members
        </Typography>
      )}
      <FormControl component="fieldset" fullWidth>
        <FormGroup
          sx={
            variant === "card"
              ? {
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  columnGap: 2,
                }
              : undefined
          }
        >
          {agents.map((agent) => (
            <FormControlLabel
              key={agent.id}
              control={
                <Checkbox
                  checked={selectedAgentIds.includes(agent.id)}
                  onChange={() => handleToggle(agent.id)}
                  disabled={disabled}
                  sx={{
                    color: colors.gray400,
                    "&.Mui-checked": { color: workspace.accent },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    color: colors.primary_dark,
                    fontSize: "0.875rem",
                  }}
                >
                  {agent.name}
                </Typography>
              }
            />
          ))}
        </FormGroup>
      </FormControl>
    </Box>
  );
};

export default ComoAgentSelector;
